import {
    CalendarOutlined,
    DeleteOutlined,
    EditOutlined,
    MailOutlined,
    PhoneOutlined,
    QuestionCircleOutlined,
    TeamOutlined,
    UserOutlined,
} from "@ant-design/icons";
import {
    Button,
    Checkbox,
    Form,
    Input,
    Modal,
    notification,
    Popconfirm,
    Select,
    Skeleton,
} from "antd";
import { useContext, useEffect, useState } from "react";
import { useQuery } from "react-query";
import HTTP from "../../services/httpService";
import CustomTable from "../common/Table";
import { TeamMangementContext } from ".";
import { weekDays } from "../../services/util";

const { Option } = Select;
const CheckboxGroup = Checkbox.Group;

function Member() {
    const staff_id = localStorage.getItem("x-dashboard-staffid");
    const [formData, setFormData] = useState({ staff_id, page: 0, limit: 10 });
    const [memberData, setMemberData] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [modalTitle, setModalTitle] = useState("Add Team Member");
    const [form] = Form.useForm();
    const { memberFormVisible, setMemberFormVisible } =
        useContext(TeamMangementContext);

    const {
        status: teamStatus,
        data: teamRes,
        refetch: getTeam,
    } = useQuery(
        "team",
        async () => {
            return await HTTP("/teams/getTeams", { staff_id });
        },
        { enabled: false },
    );

    const {
        status: shiftStatus,
        data: shiftRes,
        refetch: getShift,
    } = useQuery(
        "shift",
        async () => {
            return await HTTP("/teams/getShifts", { staff_id });
        },
        { enabled: false },
    );

    const {
        status: memberStatus,
        data: memberRes,
        refetch: getMember,
    } = useQuery(
        "member",
        async () => {
            return await HTTP("/teams/getMembers", { ...formData });
        },
        { enabled: false },
    );

    const neutralActionButtonStyle = {
        background: "var(--color-bg-elevated)",
        border: "1px solid var(--color-border)",
        borderRadius: 7,
        width: 30,
        height: 30,
    };

    const tintedActionButtonStyle = {
        background: "var(--color-error-light)",
        border: "1px solid var(--color-border)",
        borderRadius: 7,
        width: 30,
        height: 30,
    };

    const renderEmpty = (label = "Not available") => (
        <span className="team-management-empty">{label}</span>
    );

    const renderMemberIdentity = (_, record) => (
        <div className="team-management-identity">
            <div className="team-management-avatar">
                <UserOutlined />
            </div>
            <div className="team-management-identity-copy">
                <span className="team-management-identity-title">
                    {record.name || "Unnamed member"}
                </span>
                <span className="team-management-identity-subtitle">
                    Member ID {record.id}
                </span>
            </div>
        </div>
    );

    const renderContactCell = (icon, value, emptyLabel) =>
        value ? (
            <div className="team-management-contact">
                {icon}
                <span>{value}</span>
            </div>
        ) : (
            renderEmpty(emptyLabel)
        );

    const renderChip = (label, type = "primary", emptyLabel = "Not assigned") =>
        label ? (
            <span className={`team-management-chip team-management-chip-${type}`}>
                {label}
            </span>
        ) : (
            renderEmpty(emptyLabel)
        );

    const renderWorkingDays = (days) =>
        Array.isArray(days) && days.length > 0 ? (
            <div className="team-management-chip-wrap">
                {days.map((day) => {
                    const matchingDay = weekDays.find((item) => item.key == day);
                    return (
                        <span
                            key={day}
                            className="team-management-chip team-management-chip-neutral"
                        >
                            {matchingDay?.title || day}
                        </span>
                    );
                })}
            </div>
        ) : (
            renderEmpty("Working days not set")
        );

    const columns = [
        {
            title: "Name",
            dataIndex: "name",
            key: "name",
            sorter: () => {},
            render: (_, record) => renderMemberIdentity(_, record),
        },
        {
            title: "Email",
            dataIndex: "email",
            key: "email",
            sorter: () => {},
            render: (value) =>
                renderContactCell(
                    <MailOutlined className="team-management-contact-icon" />,
                    value,
                    "Email not added",
                ),
        },
        {
            title: "Phone",
            dataIndex: "phone_number",
            key: "phone_number",
            sorter: () => {},
            render: (value) =>
                renderContactCell(
                    <PhoneOutlined className="team-management-contact-icon" />,
                    value,
                    "Phone not added",
                ),
        },
        {
            title: "Team",
            key: "team_id",
            render: (text, record) => {
                const assignedTeam = teamRes?.data.find(
                    (e) => e.id == record.team_id,
                );
                return renderChip(
                    assignedTeam?.name,
                    "primary",
                    "Team not assigned",
                );
            },
        },
        {
            title: "Shift",
            key: "shift_id",
            render: (text, record) => {
                const assignedShift = shiftRes?.data.find(
                    (e) => e.id == record.shift_id,
                );
                return renderChip(
                    assignedShift?.name,
                    "accent",
                    "Shift not assigned",
                );
            },
        },
        {
            title: "Working Days",
            dataIndex: "working_days",
            key: "working_days",
            render: (value) => renderWorkingDays(value),
        },
        {
            title: "Action",
            key: "action",
            width: 96,
            render: (text, record) => {
                return (
                    <div className="team-management-action-row">
                        <Popconfirm
                            title="Delete this team member?"
                            onConfirm={() => deleteMember(record.id)}
                            icon={
                                <QuestionCircleOutlined
                                    style={{ color: "var(--color-error)" }}
                                />
                            }
                        >
                            <Button
                                type="text"
                                size="small"
                                icon={
                                    <DeleteOutlined
                                        style={{ color: "var(--color-error)" }}
                                    />
                                }
                                style={tintedActionButtonStyle}
                            />
                        </Popconfirm>
                        <Button
                            type="text"
                            size="small"
                            icon={
                                <EditOutlined
                                    style={{ color: "var(--color-primary)" }}
                                />
                            }
                            onClick={() => openEditForm(record)}
                            style={neutralActionButtonStyle}
                        />
                    </div>
                );
            },
        },
    ];

    async function deleteMember(id) {
        const { success, message } = await HTTP("/teams/deleteMember", {
            staff_id,
            id,
        });
        let type = "error";
        if (success) {
            type = "info";
        }
        notification[type]({ message });
        getMember();
    }

    function openEditForm(record) {
        form.setFieldsValue({
            name: record.name,
            id: record.id,
            email: record.email,
            phone_number: record.phone_number,
            team_id: record.team_id,
            shift_id: record.shift_id,
            working_days: record.working_days,
        });
        setModalTitle("Edit Team Member");
        setMemberFormVisible(true);
    }

    async function handleSubmit(values) {
        let url = "/teams/addMember";
        if (values.id) {
            url = "/teams/updateMember";
        }
        const { success, message } = await HTTP(url, {
            staff_id,
            ...values,
        });
        let type = "error";
        if (success) {
            type = "info";
        }
        notification[type]({ message });
        closeForm();
        getMember();
    }

    function closeForm() {
        setMemberFormVisible(false);
        setModalTitle("Add Team Member");
        form.resetFields();
    }

    useEffect(() => {
        getTeam();
        getShift();
        getMember();
    }, []);

    useEffect(() => {
        getMember();
    }, [formData]);

    useEffect(() => {
        if (memberStatus === "success") {
            const { data, total } = memberRes;
            setMemberData(data);
            setTotalRecords(total);
        }
    }, [memberStatus, memberRes]);

    function handleTableChange(pagination, _, sorter) {
        const { current, pageSize } = pagination;
        const { order, field } = sorter;
        const others = {
            page: current - 1,
            limit: pageSize,
        };
        if (order !== undefined) {
            others.sorting = order === "ascend" ? 1 : 2;
            others.sort_by = field;
        }
        setFormData({ ...formData, ...others });
    }

    return (
        <>
            {memberStatus !== "idle" && memberStatus !== "loading" ? (
                <CustomTable
                    columns={columns}
                    tableData={memberData}
                    noOfrecords={totalRecords}
                    handleTableChange={handleTableChange}
                    rowKey="id"
                    isScroll
                />
            ) : (
                <Skeleton active title={false} paragraph={{ rows: 10 }} />
            )}

            <Modal
                open={memberFormVisible}
                title={
                    <div className="team-management-modal-header">
                        <div className="team-management-modal-title">
                            {modalTitle}
                        </div>
                        <div className="team-management-modal-subtitle">
                            Configure member details, team assignment, shift,
                            and working days in one place.
                        </div>
                    </div>
                }
                footer={null}
                className="team-management-modal"
                width={760}
                destroyOnHidden
                onCancel={closeForm}
            >
                <Form
                    form={form}
                    onFinish={handleSubmit}
                    layout="vertical"
                    className="team-management-form"
                >
                    <Form.Item name="id" className="hidden">
                        <Input id="id" disabled />
                    </Form.Item>
                    <div className="team-management-form-section">
                        <div className="team-management-form-section-title">
                            Member Details
                        </div>
                        <div className="team-management-form-grid">
                            <Form.Item
                                label="Name"
                                name="name"
                                rules={[
                                    {
                                        required: true,
                                        message: "Please add the member name",
                                    },
                                ]}
                            >
                                <Input
                                    id="name"
                                    prefix={<UserOutlined />}
                                    placeholder="Enter member name"
                                />
                            </Form.Item>
                            <Form.Item
                                label="Email"
                                name="email"
                                rules={[
                                    {
                                        required: true,
                                        message: "Please add the email address",
                                    },
                                    {
                                        type: "email",
                                        message: "Invalid email format",
                                    },
                                ]}
                            >
                                <Input
                                    id="email"
                                    prefix={<MailOutlined />}
                                    placeholder="Enter email address"
                                />
                            </Form.Item>
                            <Form.Item
                                label="Phone"
                                name="phone_number"
                                rules={[
                                    {
                                        required: true,
                                        message: "Please add the phone number",
                                    },
                                ]}
                            >
                                <Input
                                    id="phone_number"
                                    prefix={<PhoneOutlined />}
                                    placeholder="Enter phone number"
                                />
                            </Form.Item>
                        </div>
                    </div>
                    <div className="team-management-form-section">
                        <div className="team-management-form-section-title">
                            Assignments
                        </div>
                        <div className="team-management-form-grid">
                            <Form.Item
                                label="Team"
                                name="team_id"
                                rules={[
                                    {
                                        required: true,
                                        message: "Please select a team",
                                    },
                                ]}
                            >
                                <Select
                                    id="team_id"
                                    placeholder="Select a team"
                                    suffixIcon={<TeamOutlined />}
                                >
                                    {teamStatus === "success" &&
                                        teamRes?.data &&
                                        teamRes.data.map((e) => (
                                            <Option key={e.id} value={e.id}>
                                                {e.name}
                                            </Option>
                                        ))}
                                </Select>
                            </Form.Item>
                            <Form.Item
                                name="shift_id"
                                label="Shift"
                                rules={[
                                    {
                                        required: true,
                                        message: "Please select a shift",
                                    },
                                ]}
                            >
                                <Select
                                    id="shift_id"
                                    placeholder="Select a shift"
                                >
                                    {shiftStatus === "success" &&
                                        shiftRes?.data &&
                                        shiftRes.data.map((e) => (
                                            <Option key={e.id} value={e.id}>
                                                {e.name}
                                            </Option>
                                        ))}
                                </Select>
                            </Form.Item>
                        </div>
                    </div>
                    <div className="team-management-form-section">
                        <div className="team-management-form-section-title">
                            Working Days
                        </div>
                        <Form.Item
                            label="Select working days"
                            name="working_days"
                            rules={[
                                {
                                    required: true,
                                    message: "Please select working days",
                                },
                            ]}
                        >
                            <CheckboxGroup
                                id="working_days"
                                className="team-management-checkbox-grid"
                            >
                                {weekDays.map((e) => (
                                    <Checkbox key={e.key} value={e.key}>
                                        <span className="team-management-checkbox-label">
                                            <CalendarOutlined />
                                            {e.title}
                                        </span>
                                    </Checkbox>
                                ))}
                            </CheckboxGroup>
                        </Form.Item>
                    </div>
                    <div className="team-management-form-actions">
                        <Button onClick={closeForm}>Cancel</Button>
                        <Button type="primary" htmlType="submit">
                            Save Member
                        </Button>
                    </div>
                </Form>
            </Modal>
        </>
    );
}
export default Member;
